<section class="content absence-page">
  <div class="content-block">
    <app-breadcrumb [title]="'absencesCatalog.breadcrumb.title' | translate"
                    [items]="['absencesCatalog.breadcrumb.section' | translate]"
                    [active_item]="'absencesCatalog.breadcrumb.title' | translate" />

    <div class="absence-filters">
      @if (hasMultipleCompanies) {
      <app-company-select class="absence-filter-field" [records]="companies"
                          [value]="filters.companiesRecordId" [includeAll]="true"
                          [label]="'orgUnitsCatalog.fields.company' | translate"
                          [placeholder]="'absencesCatalog.filters.allCompanies' | translate"
                          [allLabel]="'absencesCatalog.filters.allCompanies' | translate"
                          (valueChange)="filters.companiesRecordId = $event; onCompanyChanged()" />
      }

      <app-org-unit-tree-select class="absence-filter-field"
                                [units]="orgUnits"
                                [value]="filters.orgUnitId"
                                [placeholder]="'absencesCatalog.filters.allOrgUnits' | translate"
                                [searchPlaceholder]="'employeesCatalog.orgUnitPicker.searchPlaceholder' | translate"
                                (valueChange)="filters.orgUnitId = $event; onOrgUnitChanged()" />

      <mat-form-field class="absence-filter-field" appearance="outline" subscriptSizing="dynamic">
        <mat-icon matPrefix>badge</mat-icon>
        <input matInput
               [placeholder]="'absencesCatalog.filters.employeePlaceholder' | translate"
               [attr.aria-label]="'absencesCatalog.filters.employeePlaceholder' | translate"
               [ngModel]="employeeSearchText"
               (ngModelChange)="onEmployeeInput($event)"
               [matAutocomplete]="employeeAutocomplete" />
        <mat-autocomplete #employeeAutocomplete="matAutocomplete"
                          [displayWith]="displayEmployee"
                          (optionSelected)="onEmployeeSelected($event)">
          @for (employee of employees; track employee.employeeId) {
            <mat-option [value]="employee">{{ displayEmployee(employee) }}</mat-option>
          }
        </mat-autocomplete>
      </mat-form-field>

      <div class="absence-week-filter">
        <button mat-icon-button
                type="button"
                class="absence-week-filter__nav"
                (click)="changeWeek(-1)"
                [attr.aria-label]="'absencesCatalog.actions.previousWeek' | translate">
          <mat-icon>chevron_left</mat-icon>
        </button>
        <app-date-time-picker-field class="absence-date-filter"
                                    [label]="'absencesCatalog.filters.week' | translate"
                                    [value]="filters.weekDate"
                                    mode="date"
                                    (valueChange)="onWeekDateChanged($event)" />
        <button mat-icon-button
                type="button"
                class="absence-week-filter__nav"
                (click)="changeWeek(1)"
                [attr.aria-label]="'absencesCatalog.actions.nextWeek' | translate">
          <mat-icon>chevron_right</mat-icon>
        </button>
      </div>

      <div class="absence-filters__actions">
        <button mat-icon-button type="button" class="absence-refresh-button" (click)="refreshTable()"
                [disabled]="isLoading"
                [attr.aria-label]="'common.refresh' | translate">
          <mat-icon [class.absence-refresh-button__icon--loading]="isLoading">refresh</mat-icon>
        </button>
        <button mat-icon-button type="button" class="absence-clear-button" (click)="clearFilters()"
                [attr.aria-label]="'absencesCatalog.actions.clear' | translate">
          <mat-icon>filter_alt_off</mat-icon>
        </button>
      </div>
    </div>

    <div class="absence-matrix-card">
      @if (isLoading) {
        <app-loading-state [overlay]="false" [message]="'absencesCatalog.loading' | translate" />
      } @else if (!rows.length) {
        <div class="absence-empty">
          <span><mat-icon>event_available</mat-icon></span>
          <h3>{{ 'absencesCatalog.empty.title' | translate }}</h3>
          <p>{{ 'absencesCatalog.empty.description' | translate }}</p>
        </div>
      } @else {
        <div class="absence-list-scroll">
          <table class="absence-list">
            <thead>
              <tr>
                <th class="absence-employee-column">{{ 'absencesCatalog.table.employee' | translate }}</th>
                <th>{{ 'absencesCatalog.table.detectedAbsences' | translate }}</th>
                <th class="absence-total-column">{{ 'absencesCatalog.table.weekTotal' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (row of pagedRows; track row.employeeId) {
                <tr>
                  <td class="absence-employee-column">
                    <div class="absence-employee">
                      <span class="absence-avatar">{{ row.employeeName.charAt(0) }}</span>
                      <div>
                        <strong>{{ row.employeeName }}</strong>
                        <span>{{ row.employeeNumber }}</span>
                        <small>{{ row.orgUnitName }}</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="absence-events">
                      @for (item of absencesForRow(row); track item.result.attendanceResultId) {
                        <article class="absence-event"
                                 [class.absence-event--justified]="hasJustification(item.result)">
                          <span class="absence-event__date">
                            <strong>{{ item.day.weekday }}</strong>
                            {{ item.day.dayLabel }}
                          </span>
                          <span class="absence-event__status">
                            <mat-icon>{{ hasJustification(item.result) ? 'verified' : 'cancel' }}</mat-icon>
                            <span>
                              <strong>{{ (hasJustification(item.result) ? 'absencesCatalog.status.justified' : 'absencesCatalog.status.absent') | translate }}</strong>
                              <small>{{ absenceHours(item.result) }}</small>
                            </span>
                          </span>
                          @if (!hasJustification(item.result) && canCreateJustification) {
                            <button mat-icon-button
                                    type="button"
                                    (click)="createJustification(item.result)"
                                    [attr.aria-label]="'absencesCatalog.actions.justify' | translate">
                              <mat-icon>note_add</mat-icon>
                            </button>
                          }
                        </article>
                      }
                    </div>
                  </td>
                  <td class="absence-total-column">
                    <span class="absence-count">{{ row.cells.size }}</span>
                    <small>{{ 'absencesCatalog.table.days' | translate }}</small>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        <mat-paginator [length]="totalItems"
                       [pageIndex]="currentPage - 1"
                       [pageSize]="pageSize"
                       [pageSizeOptions]="[25, 50, 100]"
                       showFirstLastButtons
                       (page)="onPageChange($event)" />
      }
    </div>
  </div>
</section>
